let alreadyLoaded = false;
let hasInitialized = false;

const LOGIN_PATH_REGEX = /^\/(login|signin|account\/login)(\/|$)/i;
const AIDE_SCRIPT_SELECTOR = 'script[data-aide-chat="pendo"]';

// Routes rendered inside the ParishSoft iframe. These sessions authenticate with
// ParishSoftToken (localStorage) instead of the redux user token.
export const PARISHSOFT_EMBED_PATHS = [
  "/messages-embed",
  "/send-message-embed",
  "/messaging-embed",
  "/embeded-alert",
];

export const PARISHSOFT_HOST_APP = "parishsoft";

// Hardcoded fallbacks used until the backend settings API is called.
// Once the backend /api/settings endpoint is ready, call setPendoRuntimeConfig()
// with the values returned from the API, and these fallbacks will not be used.
const FALLBACK_CONFIG = {
  apiKey: "fcfa222f-df95-400b-6838-da7c83760ed6",
  aideLoaderUrl: "https://ministrybrands-aide.valuelabs.com/pendo-aide-chat-loader.min.js?v=2.6.0",
  appName: "hgs_pwa",
  enabled: true,
};

/**
 * Called once after the frontend fetches settings from the backend API.
 * Stores the config on window.__HGS_CONFIG__.pendo so getPendoConfig() picks it up.
 *
 * Expected shape from backend:
 * {
 *   apiKey: string,
 *   aideLoaderUrl: string,
 *   appName: string,   // optional, defaults to "hgs_pwa"
 *   enabled: boolean   // optional, defaults to true
 * }
 */
export function setPendoRuntimeConfig(backendSettings = {}) {
  if (!window.__HGS_CONFIG__) window.__HGS_CONFIG__ = {};
  window.__HGS_CONFIG__.pendo = {
    ...FALLBACK_CONFIG,
    ...backendSettings,
  };
}

export function getPendoEnvironment(hostname = window.location.hostname) {
  const host = (hostname || "").toLowerCase();
  if (host.includes("localhost") || host.includes("dev")) return "dev";
  if (host.includes("uat") || host.includes("staging") || host.includes("test")) return "uat";
  return "prod";
}

/**
 * True when the app is running inside the ParishSoft iframe. Those sessions authenticate
 * with ParishSoftToken (localStorage) and never populate the redux user domain.
 */
export function isParishSoftEmbedSession({ windowRef = window, storageRef = window.localStorage } = {}) {
  const pathname = (windowRef.location && windowRef.location.pathname) || "";
  const onEmbedRoute = PARISHSOFT_EMBED_PATHS.some((path) => pathname.startsWith(path));
  if (!onEmbedRoute) return false;

  try {
    const hasToken = !!storageRef.getItem("ParishSoftToken");
    const flagged = storageRef.getItem("isParishSoftUser") === "true";
    return hasToken || flagged;
  } catch (e) {
    return false;
  }
}

export function getPendoConfig() {
  // Prefer runtime config injected from the backend settings API.
  // Falls back to hardcoded defaults until the API call completes.
  const runtime = window.__HGS_CONFIG__?.pendo || {};

  return {
    enabled: runtime.enabled !== undefined ? runtime.enabled : FALLBACK_CONFIG.enabled,
    environment: getPendoEnvironment(),
    appName: runtime.appName || FALLBACK_CONFIG.appName,
    apiKey: runtime.apiKey || FALLBACK_CONFIG.apiKey,
    aideLoaderUrl: runtime.aideLoaderUrl || FALLBACK_CONFIG.aideLoaderUrl,
  };
}

export function toIsoDate(value) {
  if (!value) return value;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toISOString().substring(0, 10);
}

export function toIsoDateTime(value) {
  if (!value) return value;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toISOString().substring(0, 19).replace("T", " ");
}

export function toIsoDateTimeAmPm(value) {
  if (!value) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const datePart = date.toISOString().substring(0, 10);
  let hours = date.getUTCHours();
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  const seconds = String(date.getUTCSeconds()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${datePart} ${String(hours).padStart(2, "0")}:${minutes}:${seconds} ${ampm}`;
}

function buildFullName(firstName, lastName) {
  const first = firstName ? String(firstName).trim() : "";
  const last = lastName ? String(lastName).trim() : "";
  return [first, last].filter(Boolean).join(" ") || null;
}

function resolveFullName(fullName, firstName, lastName) {
  const normalizedFullName = fullName ? String(fullName).trim() : "";
  return normalizedFullName || buildFullName(firstName, lastName);
}

function toNullable(value) {
  return value === undefined ? null : value;
}

function toNumberOrNull(value) {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function getKeyCaseInsensitive(source, key) {
  if (!source || typeof source !== "object") return undefined;
  if (Object.prototype.hasOwnProperty.call(source, key)) return source[key];
  const match = Object.keys(source).find((k) => k.toLowerCase() === key.toLowerCase());
  return match ? source[match] : undefined;
}

function getBooleanFeature(source, keys = []) {
  for (const key of keys) {
    const directValue = getKeyCaseInsensitive(source, key);
    if (typeof directValue === "boolean") return directValue;
    const nestedValue = getKeyCaseInsensitive(source?.features, key);
    if (typeof nestedValue === "boolean") return nestedValue;
  }
  return null;
}

export function normalizeUserRoleLevel(roleValue) {
  if (roleValue === undefined || roleValue === null) return null;

  const raw = String(roleValue).trim().toLowerCase();
  if (!raw) return null;

  if (["4", "admin", "administrator", "universal_admin"].includes(raw)) {
    return "Admin";
  }

  if (["1", "student", "pupil"].includes(raw)) {
    return "Student";
  }

  if (["2", "3", "staff", "faculty", "member", "user", "manager", "leader"].includes(raw)) {
    return "Staff";
  }

  return null;
}

export function getVisitorTimeZone() {
  const timezone = Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone;
  return timezone || null;
}

export function getIsMobileVisitor(windowRef = window) {
  return windowRef.innerWidth <= 768;
}

export function deriveEnabledChannels(source = {}) {
  const explicitChannels =
    getKeyCaseInsensitive(source, "enabledChannels") ||
    getKeyCaseInsensitive(source, "EnabledChannels");

  if (typeof explicitChannels === "string" && explicitChannels.trim()) {
    const allowed = ["sms", "email", "voice"];
    const parsed = explicitChannels
      .split(",")
      .map((part) => part.trim().toLowerCase())
      .filter((part) => allowed.includes(part));
    return parsed.length ? [...new Set(parsed)].join(",") : null;
  }

  const channels = [];

  if (
    getBooleanFeature(source, ["sms", "smsEnabled", "isSmsEnabled", "enableSms", "canSms", "allowSms", "hasSms"]) === true
  ) {
    channels.push("sms");
  }

  if (
    getBooleanFeature(source, [
      "email",
      "emailEnabled",
      "isEmailEnabled",
      "enableEmail",
      "canEmail",
      "allowEmail",
      "hasEmail",
    ]) === true
  ) {
    channels.push("email");
  }

  if (
    getBooleanFeature(source, [
      "voice",
      "voiceEnabled",
      "isVoiceEnabled",
      "enableVoice",
      "canVoice",
      "allowVoice",
      "hasVoice",
    ]) === true
  ) {
    channels.push("voice");
  }

  return channels.length ? channels.join(",") : null;
}

export function buildPendoAccount(user = {}) {
  return {
    id: user?.id || null,
    name: user?.name || null,
    type: user?.type || null,
    typeId: user?.typeId ?? null,
    email: user?.email || null,
    address: user?.address || null,
    city: user?.city || null,
    state: user?.state || null,
    zipCode: user?.zipCode || null,
    country: user?.country || null,
    creationDate: toIsoDate(user?.creationDate || null),
    trialExpirationDate: toIsoDate(user?.trialExpirationDate || null),
    plan: user?.plan || null,
    product: user?.product || null,
    product_id: toNumberOrNull(user?.productId ?? user?.ProductId),
    total_groups: toNumberOrNull(user?.totalGroups ?? user?.TotalGroups),
    total_members: toNumberOrNull(user?.totalMembers ?? user?.TotalMembers),
    enabled_channels: deriveEnabledChannels(user),
    reseller: user?.reseller || null,
    CM_IsLegacyHGS: false,
    brand: user?.brand || user?.product || user?.reseller || null,
    plan_tier: user?.plan_tier || user?.callPlanName || null,
    billing_cadence: user?.billing_cadence || null,
    billing_model: user?.billing_model || null,
    credit_balance: user?.credit_balance ?? null,
    created_at: user?.creationDate || null,
  };
}

export function buildPendoVisitor(user = {}, account = {}, appName = "hgs_pwa") {
  const fullName = resolveFullName(user?.fullName, user?.firstName, user?.lastName);
  return {
    id: user?.id || null,
    userName: user?.userName || null,
    name: fullName,
    email: user?.email || user?.contactEmail || null,
    city: user?.city || (user?.location && user?.location.city) || null,
    state: user?.state || (user?.location && user?.location.state) || null,
    zipCode: user?.zipCode || null,
    organization: account?.name || null,
    phone: user?.phone || user?.mobile || user?.phoneNumber || null,
    fullName,
    firstName: user?.firstName || null,
    lastName: user?.lastName || null,
    appName,
    display_role: user?.role || null,
    user_role_level: normalizeUserRoleLevel(
      user?.userRoleLevel ?? user?.UserRoleLevel ?? user?.role
    ),
    is_mobile: getIsMobileVisitor(),
    timezone: getVisitorTimeZone(),
    created_at: toIsoDateTimeAmPm(user?.created_at) || null,
    last_login_at: toIsoDateTimeAmPm(user?.last_login_at) || null,
    language: "en_US",
  };
}

export function buildPendoIdentityFromUser(user = {}, config = getPendoConfig(), metadata = {}) {
  const account = buildPendoAccount({
    id: "CM-" + user?.customerId,
    name: user?.customerName,
    type: user?.accountTypeName || null,
    typeId: user?.accountTypeId ?? null,
    email: user?.customerEmail || null,
    address: user?.customerAddress || null,
    city: user?.churchCity || user?.ChurchCity || null,
    state: user?.churchState || user?.ChurchState || null,
    zipCode: user?.churchZip || user?.ChurchZip || null,
    country: user?.churchCountry || user?.ChurchCountry || null,
    creationDate: user?.agreementStartDate || user?.agreement_start_date || null,
    trialExpirationDate: user?.freeTrialExpiry || user?.FreeTrialExpiry || null,
    plan: user?.callPlanName || null,
    product: user?.product || user?.reseller || "PWA Product",
    productId: user?.productId ?? user?.ProductId ?? metadata?.productId ?? null,
    totalGroups:
      user?.totalGroups ??
      user?.TotalGroups ??
      metadata?.totalGroups ??
      null,
    totalMembers:
      user?.totalMembers ??
      user?.TotalMembers ??
      metadata?.totalMembers ??
      null,
    enabledChannels:
      metadata?.enabledChannels ??
      user?.enabledChannels ??
      user?.EnabledChannels ??
      null,
    reseller: user?.reseller || user?.product || "PWA Product",
    brand: user?.brand || user?.product || user?.reseller || null,
    plan_tier: user?.plan_tier || user?.callPlanName || null,
    billing_cadence: user?.billing_cadence || null,
    billing_model: user?.billing_model || null,
    credit_balance: user?.credit_balance ?? null,
  });

  const visitor = buildPendoVisitor(
    {
      id: "CM-" + user?.userId,
      userName: user?.userName || null,
      fullName: buildFullName(user?.firstName, user?.lastName),
      firstName: user?.firstName || null,
      lastName: user?.lastName || null,
      email: user?.email || user?.emailAddress || null,
      city: user?.city || null,
      state: user?.state || null,
      zipCode: user?.zipCode || user?.zip || null,
      phone: user?.phone || user?.mobile || user?.phoneNumber || null,
      role: user?.userRoleLevel ?? user?.UserRoleLevel ?? user?.userType ?? null,
      created_at: user?.dateCreated || null,
      last_login_at: user?.lastLoginAt || null,
    },
    account,
    config.appName
  );

  return { account, visitor };
}

export function shouldSkipPendoInitialization(pathname, account, visitor) {
  const onExplicitLoginRoute = LOGIN_PATH_REGEX.test(pathname);
  const noIdentifiersYet = !visitor?.id || !account?.id;
  const onRootDuringLogin = pathname === "/" && noIdentifiersYet;
  return onExplicitLoginRoute || onRootDuringLogin || !visitor?.id || !account?.id;
}

function getPendoAgentUrl(apiKey) {
  return `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;
}

function ensurePendoAgent(windowRef, documentRef, apiKey) {
  if (windowRef.pendo && typeof windowRef.pendo.initialize === "function") {
    return true;
  }

  if (!windowRef.pendo) {
    windowRef.pendo = {};
  }

  const queueMethods = ["initialize", "identify", "updateOptions", "pageLoad", "track"];
  windowRef.pendo._q = windowRef.pendo._q || [];
  queueMethods.forEach((method) => {
    if (typeof windowRef.pendo[method] !== "function") {
      windowRef.pendo[method] = function () {
        const args = Array.prototype.slice.call(arguments, 0);
        windowRef.pendo._q[method === "initialize" ? "unshift" : "push"]([method].concat(args));
      };
    }
  });

  const pendoAgentUrl = getPendoAgentUrl(apiKey);
  if (documentRef.querySelector(`script[src="${pendoAgentUrl}"]`)) {
    return true;
  }

  const script = documentRef.createElement("script");
  script.async = true;
  script.src = pendoAgentUrl;

  const firstScript = documentRef.getElementsByTagName("script")[0];
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    (documentRef.body || documentRef.head).appendChild(script);
  }

  return false;
}

export function loadAideLoader({ windowRef = window, documentRef = document } = {}) {
  if (!windowRef?.document || !documentRef) return null;

  const config = getPendoConfig();
  if (!config.enabled) return null;

  const existingScript = documentRef.querySelector(AIDE_SCRIPT_SELECTOR);
  if (existingScript) return existingScript;

  const script = documentRef.createElement("script");
  script.src = `${config.aideLoaderUrl}?v=${Date.now()}`;
  script.async = true;
  script.defer = true;
  script.crossOrigin = "anonymous";
  script.setAttribute("data-aide-chat", "pendo");
  (documentRef.body || documentRef.head).appendChild(script);
  return script;
}

export function installAideRejectionHandler(windowRef = window) {
  function suppressAideRejection(e) {
    const reason = e && e.reason;
    const msg = (reason && (reason.message || reason.toString())) || "";
    if (msg.includes("Failed to fetch") || msg.includes("pendo-asset-manifest")) {
      e.preventDefault();
    }
  }

  windowRef.addEventListener("unhandledrejection", suppressAideRejection);
  return () => windowRef.removeEventListener("unhandledrejection", suppressAideRejection);
}

export function cleanupPendo({ windowRef = window, documentRef = document } = {}) {
  windowRef.pendoDisabled = true;
  hasInitialized = false;

  const aideScript = documentRef.querySelector(AIDE_SCRIPT_SELECTOR);
  if (aideScript && aideScript.parentNode) {
    aideScript.parentNode.removeChild(aideScript);
  }

  const aideNodes = documentRef.querySelectorAll(
    '[id*="aide"], [class*="aide"], iframe[src*="valuelabs.com"], iframe[src*="pendo"]'
  );
  aideNodes.forEach((node) => {
    if (node.parentNode) node.parentNode.removeChild(node);
  });

  const pendoBubbles = documentRef.querySelectorAll('[class*="pendo"]');
  pendoBubbles.forEach((node) => {
    node.style.display = "none";
  });

  if (windowRef.pendo && typeof windowRef.pendo.disable === "function") {
    windowRef.pendo.disable();
  }
}

export function initPendo(account = {}, visitor = {}, options = {}) {
  const windowRef = options.windowRef || window;
  const documentRef = options.documentRef || document;
  const config = getPendoConfig();
  const visitorWithLanguage = {
    ...visitor,
    language: visitor?.language || "en_US",
    is_mobile: getIsMobileVisitor(windowRef),
    timezone: getVisitorTimeZone(),
    user_role_level: normalizeUserRoleLevel(
      visitor?.user_role_level ?? visitor?.userRoleLevel ?? visitor?.display_role ?? visitor?.role
    ),
  };
  const accountWithMetadata = {
    ...account,
    total_groups: toNumberOrNull(account?.total_groups ?? account?.totalGroups),
    total_members: toNumberOrNull(account?.total_members ?? account?.totalMembers),
    enabled_channels: toNullable(account?.enabled_channels ?? deriveEnabledChannels(account)),
    product_id: toNumberOrNull(account?.product_id ?? account?.productId),
  };

  if (!config.enabled || !config.apiKey) return;

  windowRef.pendoDisabled = false;

  if (shouldSkipPendoInitialization(windowRef.location.pathname, account, visitor)) {
    return;
  }

  if (!alreadyLoaded) {
    ensurePendoAgent(windowRef, documentRef, config.apiKey);
    alreadyLoaded = true;
  }

  if (!hasInitialized && windowRef.pendo && typeof windowRef.pendo.initialize === "function") {
    windowRef.pendo.initialize({ account: accountWithMetadata, visitor: visitorWithLanguage });
    hasInitialized = true;
    return;
  }

  if (windowRef.pendo && typeof windowRef.pendo.updateOptions === "function") {
    windowRef.pendo.updateOptions({ account: accountWithMetadata, visitor: visitorWithLanguage });
    return;
  }

  if (windowRef.pendo && typeof windowRef.pendo.initialize === "function") {
    windowRef.pendo.initialize({ account: accountWithMetadata, visitor: visitorWithLanguage });
    hasInitialized = true;
  }
}

export default initPendo;
